import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { MiddlewareInjectToken } from '../constants/tokens';
import { QuoteStreamingHandler, QuoteStreamingHandlerOptions } from '../QuoteStreamingHandler';
import { Apis, MiddlewareConfig } from '../types';
import { OrderStreamingResponse, QuoteStreamingDataSource } from '../types/WebSocket.type';
import { WebSocketHandler, WebSocketHandlerOptions } from '../WebSocketHandler';

@injectable()
export class WebSocketApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
    ) {
        super(apis, config);
    }

    private getMdasWebSocketUrl() {
        const umaApiUrl = this.apis.uma?.getApiClient().getConfig().baseUrl;
        if (!umaApiUrl) {
            return '';
        }

        const token = this.apis.uma?.getApiClient().getAuthData()?.accessToken || '';
        const url = new URL(umaApiUrl);
        const baseHost = `${url.protocol}//${url.host}`;
        const wsUrl = baseHost.replace('https://', 'wss://').replace('http://', 'ws://');
        return `${wsUrl}/aes-ws/wssub?token=${encodeURIComponent(token)}`;
    }

    private getWatchmenWebSocketUrl(endpoint: string) {
        const watchmenApiUrl = this.apis.orbis.getApiClient().getConfig().baseUrl;
        if (!watchmenApiUrl) {
            return '';
        }

        const token = this.apis.orbis.getApiClient().getAuthData()?.accessToken || '';
        const url = new URL(watchmenApiUrl);
        const baseHost = `${url.protocol}//${url.host}`;
        const wsUrl = baseHost.replace('https://', 'wss://').replace('http://', 'ws://');
        return `${wsUrl}/${endpoint}?auth=${escape('SessionId ' + token)}`;
    }

    /**
     * Connect to WebSocket for real-time quote streaming.
     * Automatically selects MDAS (UMA auth) or Watchmen WebSocket based on configuration.
     * Returns a QuoteStreamingHandler that normalizes messages from either source into
     * a unified QuoteStreamingData format, with protocol-aware subscribe / unsubscribe methods.
     */
    quotes(options?: Omit<QuoteStreamingHandlerOptions, 'dataSource' | 'getUrl'>): QuoteStreamingHandler {
        if (this.config.useAes) {
            return new QuoteStreamingHandler({
                dataSource: QuoteStreamingDataSource.Mdas,
                getUrl: () => this.getMdasWebSocketUrl(),
                ...options,
            });
        }

        return new QuoteStreamingHandler({
            dataSource: QuoteStreamingDataSource.Watchmen,
            getUrl: () => this.getWatchmenWebSocketUrl('stream/json'),
            ...options,
        });
    }

    orders(options?: WebSocketHandlerOptions<OrderStreamingResponse>): WebSocketHandler<OrderStreamingResponse> {
        const getUrl = () => this.getWatchmenWebSocketUrl('orders/feed');
        const wsHandler = new WebSocketHandler<OrderStreamingResponse>(getUrl(), {
            ...options,
            onReconnect: (event) => {
                wsHandler.updateUrl(getUrl());
                options?.onReconnect?.(event);
            },
        });
        return wsHandler;
    }
}
